import { editGreenIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { CustomButton } from '@/shared/ui/components';
import { generateAddress } from '@/shared/lib';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import type { CreateReturnShippingInfoProps } from './createReturnShippingInfo.types';
import './createReturnShippingInfo.scss';

/**
 * Placeholder edit callback serving as the default onEdit of the return-shipping info card. Runs no statements and produces no value.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

const CreateReturnShippingInfo = ({
  data,
  onEdit = NOOP,
  isHideEdit = false,
  listStatesUS,
  listStatesCA,
  isSender = false,
  className,
}: CreateReturnShippingInfoProps) => {
  /**
   * Builds the heading text of a return-shipping contact card from the contact's name and company. Collects whichever of the two values are present, returns the double-hyphen-bullet placeholder when neither exists, and otherwise joins them with a hyphen.
   *
   * @returns The card title string, or the placeholder when no name or company is set.
   */
  const renderTitle = () => {
    const { name, company } = data || {};
    const contents = [];

    if (name) {
      contents.push(name);
    }

    if (company) {
      contents.push(company);
    }

    if (contents.length === 0) {
      return DOUBLE_HYPHEN_BULLET;
    }

    return contents.join(' - ');
  };

  return (
    <div className={classNames('create-return-shipping-info', className)}>
      <div className="header">
        <div className="text">{isSender ? t`Sender information` : t`Recipient information`}</div>
        {!isHideEdit && (
          <CustomButton
            id="edit-btn"
            icon={<ReactSVG src={editGreenIcon} />}
            onClick={() => {
              onEdit();
            }}
            type="secondary"
            size="small"
            style="default"
            ariaLabel={`${t`Edit`} ${isSender ? t`Sender information` : t`Recipient information`}`}
          />
        )}
      </div>
      <div className="body">
        <div className="text bold">{renderTitle()}</div>
        <div className="text">{generateAddress({ data, listStatesUS, listStatesCA })}</div>
        <div className="text">{data?.phone || DOUBLE_HYPHEN_BULLET}</div>
        <div className="text">{data?.email || DOUBLE_HYPHEN_BULLET}</div>
      </div>
    </div>
  );
};

export default CreateReturnShippingInfo;
